import * as React from "react";
import { useState, useEffect } from "react";
import { Page } from "../../components/Page";
import SimpleTable from "../../components/table/SimpleTable";
import {
  getBlockData,
  getCurrentUser,
  getFloorData,
  insertFloorData,
  updateFloorData,
  deleteFloorData
} from "../../api-config/config";
import SnackBar from "../../components/SnackBar/SnackBar";
import LoaderWrapper from "../../components/LoaderWrapper/LoaderWrapper";


function FloorListGrid(props: any) {
  const { row, floorSubmitted, blockList, setLoading } = props;

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [floorTitle, setFloorTitle] = useState("");
  const [modalBlockId, setModalBlockId] = useState(0);
  const [editingFloor, setEditingFloor] = useState<any>(null);

  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "success" as "success" | "error",
  });

  const showSnackbar = (message: string, type: "success" | "error") => {
    setSnackbar({ visible: true, message, type });
  };


  const handleSubmit = async () => {
    if (!modalBlockId) {
      showSnackbar("Please select a block", "error");
      return;
    }

    if (!floorTitle.trim()) {
      showSnackbar("Please enter floor name", "error");
      return;
    }

    try {
      setLoading(true);
      const res = editingFloor
        ? await updateFloorData({
            floor_id: editingFloor.id,
            floor_name: floorTitle,
            block_id: modalBlockId,
          })
        : await insertFloorData(String(modalBlockId), floorTitle);

      if (res?.status_message === "failed") {
        setLoading(false);
        showSnackbar(res.message || "Operation failed", "error");
        return;
      }

      showSnackbar(
        editingFloor ? "Floor updated successfully" : "Floor added successfully",
        "success"
      );

      floorSubmitted();
      resetForm();
    } catch {
      setLoading(false);
      showSnackbar("Something went wrong", "error");
    }
  };


  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteFloorData(editingFloor.id);
      showSnackbar("Floor deleted successfully", "success");
      floorSubmitted();
      setDeleteOpen(false);
      setEditingFloor(null);
    } catch {
      setLoading(false);
      showSnackbar("Failed to delete floor", "error");
    }
  };


  const resetForm = () => {
    setOpen(false);
    setDeleteOpen(false);
    setFloorTitle("");
    setModalBlockId(0);
    setEditingFloor(null);
  };


  const rows =
    row?.map((item: any, index: number) => ({
      serial: index + 1,
      floor: item.floor_name,
      rooms: item.rooms?.length || 0,
      status: item.is_active ? "Active" : "Inactive",
      raw: item,
    })) || [];

  const columns = [
    { header: "#", accessor: "serial", width: "5%" },
    { header: "Floor Name", accessor: "floor", width: "30%" },
    { header: "Rooms", accessor: "rooms", width: "15%" },
    { header: "Status", accessor: "status", width: "15%" },
    {
      header: "Actions",
      accessor: "actions",
      width: "15%",
      render: (_: any, r: any) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              setEditingFloor(r.raw);
              setFloorTitle(r.raw.floor_name);
              setModalBlockId(r.raw.block_id);
              setOpen(true);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </span>

          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              setEditingFloor(r.raw);
              setDeleteOpen(true);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill="red">
              <path d="M 14.984375 2.4863281 A 1.0001 1.0001 0 0 0 14 3.5 L 14 4 L 8.5 4 A 1.0001 1.0001 0 0 0 7.4863281 5 L 6 5 A 1.0001 1.0001 0 1 0 6 7 L 24 7 A 1.0001 1.0001 0 1 0 24 5 L 22.513672 5 A 1.0001 1.0001 0 0 0 21.5 4 L 16 4 L 16 3.5 A 1.0001 1.0001 0 0 0 14.984375 2.4863281 z M 6 9 L 7.7929688 24.234375 C 7.9109687 25.241375 8.7633438 26 9.7773438 26 L 20.222656 26 C 21.236656 26 22.088031 25.241375 22.207031 24.234375 L 24 9 L 6 9 z"/>
            </svg>
          </span>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "0px 20px" }}>
      <div className="roomtype-header">
        <h2 className="roomtype-title">Floor List</h2>
        <div className="roomtype-btns">
          <button className="my-custom-btn" onClick={() => setOpen(true)}>
            Add Floor
          </button>
        </div>
      </div>

      <SimpleTable columns={columns} data={rows} />

      {open && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingFloor ? "Edit Floor" : "Add Floor"}
              </h3>
            </div>

            <div className="modal-content" style={{ padding: 16 }}>
              <select
                className="modal-input"
                value={modalBlockId}
                onChange={(e) => setModalBlockId(Number(e.target.value))}
              >
                <option value={0}>Select Block</option>
                {blockList.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.block_name}
                  </option>
                ))}
              </select>

              <input
                className="modal-input"
                placeholder="Enter Floor Name"
                value={floorTitle}
                onChange={(e) => setFloorTitle(e.target.value)}
              />
            </div>

            <div className="modal-footer">
              <button className="my-custom-btn" onClick={handleSubmit}>
                Save
              </button>
              <button className="my-custom-btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="delete-modal-overlay" onClick={() => setDeleteOpen(false)}>
          <div className="delete-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-modal-title">Are you sure?</h3>
            <p className="delete-modal-desc">
              Floor <span>{editingFloor?.floor_name}</span> will be permanently deleted.
            </p>

            <div className="delete-modal-actions">
              <button className="delete-btn-cancel" onClick={() => setDeleteOpen(false)}>
                No
              </button>
              <button className="delete-btn-confirm" onClick={handleDelete}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      <SnackBar
        visible={snackbar.visible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar(s => ({ ...s, visible: false }))}
      />
    </div>
  );
}


export default function NewFloorList() {
  const [floorList, setfloorList] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any>({});
  const [selectedBlockId, setSelectedBlockId] = useState(0);
  const [blockList, setBlockList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const loggedUser = getCurrentUser();

  const getfloorList = (block_id: number) => {
    setLoading(true);
    return getFloorData(block_id).then(
      (res: any) => {
        if (res?.status_message === "success") {
          setfloorList(res.data || []);
        }
        setLoading(false);
      },
      (error: any) => {
        console.log("error", error);
        setLoading(false);
      }
    );
  };

  function handleBlockChange(value: any) {
    const block = blockList.find((b: any) => b.id === Number(value));
    if (block) {
      setSelectedBlock(block);
      setSelectedBlockId(block.id);
    }
  }

  function getBlockList(hotel_code: any) {
    setLoading(true);
    getBlockData(hotel_code).then(
      (response: any) => {
        setBlockList(response.data || []);
        if (response.data?.length) {
          setSelectedBlock(response.data[0]);
          setSelectedBlockId(response.data[0].id);
        }
        setLoading(false);
      },
      (error: any) => {
        console.log("error", error);
        setLoading(false);
      }
    );
  }

  function onFloorAdded() {
    if (selectedBlock?.id) {
      getfloorList(selectedBlock.id);
    }
  }

  useEffect(() => {
    getBlockList(loggedUser?.hotel_code);
  }, []);

  useEffect(() => {
    if (selectedBlock?.id) {
      getfloorList(selectedBlock.id);
    }
  }, [selectedBlock]);

  return (
    <Page title="Home">
      {loading && <LoaderWrapper />}
      <div style={{ padding: "0px 20px" }}>
        <select
          className="custom-dropdown"
          value={selectedBlockId || 0}
          onChange={(e) => handleBlockChange(e.target.value)}
        >
          <option value="0">Select Block</option>
          {blockList.map((list: any) => (
            <option key={list.id} value={list.id}>
              Block {list.block_name}
            </option>
          ))}
        </select>
      </div>

      <FloorListGrid
        row={floorList}
        blockList={blockList}
        floorSubmitted={onFloorAdded}
        setLoading={setLoading}
      />
    </Page>
  );
}